import type { Slide, Node } from '../../../types';
import { logger } from '../../../utils/logger';

interface UseSlideOperationsProps {
  deckId: string;
  currentSlideId: number | null;
  currentSlideIndex: number;
  slides: Slide[];
  setSlides: (slides: Slide[]) => void;
  nodes: Node[];
  setCurrentSlideIndex: (index: number) => void;
  setIsAnimating: (animating: boolean) => void;
  setAnimatingNodeIds: (ids: Set<string>) => void;
  setShowDeleteConfirm: (show: boolean) => void;
  apiUrl: (path: string) => string;
  loadSlideNodes: (slide: Slide) => Promise<Node[]>;
  flushUpdateQueue: () => Promise<void>;
}

/**
 * Hook for slide CRUD operations
 *
 * Handles:
 * - Creating new slides
 * - Copying slides with all nodes
 * - Deleting slides
 * - Slide-level state management
 */
export function useSlideOperations({
  deckId,
  currentSlideId,
  currentSlideIndex,
  slides,
  setSlides,
  nodes,
  setCurrentSlideIndex,
  setIsAnimating,
  setAnimatingNodeIds,
  setShowDeleteConfirm,
  apiUrl,
  loadSlideNodes,
  flushUpdateQueue,
}: UseSlideOperationsProps) {

  const addNewSlide = async () => {
    if (!deckId) return;

    try {
      setIsAnimating(true);

      const newPosition = currentSlideIndex + 1;

      const response = await fetch(apiUrl(`/${deckId}/slides`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          slide: {
            position: newPosition,
            locked: false,
            z_index: 1
          }
        }),
      });

      if (!response.ok) throw new Error('Failed to create slide');

      const data = await response.json();
      const newSlide = data.slide;

      const updatedSlides = [...slides];
      updatedSlides.splice(newPosition, 0, newSlide);
      setSlides(updatedSlides);

      setCurrentSlideIndex(newPosition);
      await loadSlideNodes(newSlide);

      setTimeout(() => {
        setIsAnimating(false);
      }, 400);
    } catch (error) {
      console.error('Failed to add new slide:', error);
      setIsAnimating(false);
    }
  };

  const copyCurrentSlide = async () => {
    if (!currentSlideId || !deckId) return;

    try {
      logger.info('copy', 'Starting slide copy');
      setIsAnimating(true);

      const newPosition = currentSlideIndex + 1;
      logger.debug('copy', `Creating slide at position ${newPosition}`);
      const response = await fetch(apiUrl(`/${deckId}/slides`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          slide: {
            position: newPosition,
            locked: slides[currentSlideIndex].locked,
            z_index: 1
          }
        }),
      });

      if (!response.ok) throw new Error('Failed to create slide');

      const data = await response.json();
      const newSlide = data.slide;
      logger.debug('copy', `Created slide ${newSlide.id}`);

      const copyPromises = nodes.map(async (node) => {
        const nodeData: any = {
          node_type: node.type,
          position: node.position,
          size: node.size,
          z_index: node.zIndex,
          frame_style: node.data.frameStyle,
        };

        if (node.type === 'text') {
          nodeData.content = node.data.content;
          nodeData.style = 'style' in node.data ? node.data.style : {};
          nodeData.sender_name = 'senderName' in node.data ? node.data.senderName : undefined;
          nodeData.avatar_url = 'avatarUrl' in node.data ? node.data.avatarUrl : undefined;
          nodeData.avatar_emoji = 'avatarEmoji' in node.data ? node.data.avatarEmoji : undefined;
          nodeData.background_color = 'backgroundColor' in node.data ? node.data.backgroundColor : undefined;
          nodeData.work_in_progress = 'workInProgress' in node.data ? node.data.workInProgress : undefined;
          nodeData.border_radius = 'borderRadius' in node.data ? node.data.borderRadius : undefined;
          nodeData.wip_banner_offset = 'wipBannerOffset' in node.data ? node.data.wipBannerOffset : undefined;
        } else if (node.type === 'iframe') {
          nodeData.source_url = node.data.content;
          nodeData.title = 'title' in node.data ? node.data.title : '';
          nodeData.work_in_progress = 'workInProgress' in node.data ? node.data.workInProgress : undefined;
          nodeData.border_radius = 'borderRadius' in node.data ? node.data.borderRadius : undefined;
          nodeData.wip_banner_offset = 'wipBannerOffset' in node.data ? node.data.wipBannerOffset : undefined;
          nodeData.iframe_zoom = 'iframeZoom' in node.data ? node.data.iframeZoom : undefined;
        } else {
          nodeData.source_url = node.data.content;
          nodeData.title = 'title' in node.data ? node.data.title : '';
          nodeData.work_in_progress = 'workInProgress' in node.data ? node.data.workInProgress : undefined;
          nodeData.border_radius = 'borderRadius' in node.data ? node.data.borderRadius : undefined;
          nodeData.wip_banner_offset = 'wipBannerOffset' in node.data ? node.data.wipBannerOffset : undefined;
        }

        return fetch(apiUrl(`/slides/${newSlide.id}/nodes`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ node: nodeData }),
        });
      });

      logger.debug('copy', `Copying ${nodes.length} nodes`);
      await Promise.all(copyPromises);
      logger.debug('copy', 'Nodes copied');

      const updatedSlides = [...slides];
      updatedSlides.splice(newPosition, 0, newSlide);
      setSlides(updatedSlides);
      logger.debug('copy', `Slides array updated, length: ${updatedSlides.length}`);

      logger.info('copy', `Navigating to new slide at index ${newPosition}`);
      setCurrentSlideIndex(newPosition);
      const loadedNodes = await loadSlideNodes(newSlide);
      logger.info('copy', 'Navigation complete');

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const nodeIdsToAnimate = new Set(loadedNodes.map(n => n.id));
          setAnimatingNodeIds(nodeIdsToAnimate);
          logger.debug('copy', `Animating ${nodeIdsToAnimate.size} nodes`);

          setTimeout(() => {
            logger.debug('copy', 'Animation complete');
            setAnimatingNodeIds(new Set());
            setIsAnimating(false);
          }, 500);
        });
      });
    } catch (error) {
      logger.error('copy', 'Failed to copy slide', error);
      setIsAnimating(false);
    }
  };

  const confirmDeleteSlide = () => {
    if (!currentSlideId || slides.length <= 1) {
      return;
    }
    setShowDeleteConfirm(true);
  };

  const deleteCurrentSlide = async () => {
    if (!currentSlideId) return;

    await flushUpdateQueue();

    try {
      const response = await fetch(apiUrl(`/${deckId}/slides/${currentSlideId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error('Failed to delete slide');

      const updatedSlides = slides.filter(s => s.id !== currentSlideId);
      setSlides(updatedSlides);

      const newIndex = currentSlideIndex >= updatedSlides.length
        ? updatedSlides.length - 1
        : currentSlideIndex;

      setCurrentSlideIndex(newIndex);
      await loadSlideNodes(updatedSlides[newIndex]);

      logger.info('api', `Deleted slide ${currentSlideId}`);
    } catch (error) {
      logger.error('api', 'Failed to delete slide', error);
    }
  };

  return {
    addNewSlide,
    copyCurrentSlide,
    confirmDeleteSlide,
    deleteCurrentSlide,
  };
}
